import { create } from 'zustand'
import { supabase } from '../supabaseClient'
import { getUserId } from '../lib/workos'
import { callTool } from '../lib/tools'
import useNetwork from './useNetwork'
import useNode from './useNode'
import useServices from './useServices'
import useNodeMetadata from './useNodeMetadata'
import useSetPlaces from './useSetPlaces'
import useUpgrades from './useUpgrades'
import useOrgMembers from './useOrgMembers'
import useBilling from './useBilling'
import useUsage from './useUsage'
import useOrgInvites from './useOrgInvites'
import { mayListMembers } from '../utilities/memberRoster'
import type { OrgRole } from '../utilities/orgRole'
import { userText } from '../utilities/userLabel'
import { activeOrgKey, defaultOrg } from '../utilities/defaultOrg'

export interface OrganizationRecord {
  id: string
  name: string | null // null on a personal org its owner hasn't named — see orgLabel
  slug: string | null
  personal: boolean
  role: string // 'owner' | 'admin' | 'member' — the caller's role in this org
  ownerName: string | null // the org owner's profile name (get_org_owners), null if they have none
  ownerEmail: string | null
  ownerAvatarUrl: string | null // the org owner's profile avatar, null if none
}

type OwnerRow = { org_id: string; full_name: string | null; email: string | null; avatar_url: string | null }

// Owner identity + avatar for every org the caller belongs to. get_org_owners is
// added by router migration 0091; until it is applied the RPC does not exist, so
// fall back to the avatar-only original rather than losing every org avatar in
// the window between the app deploy and the migration. Delete the fallback with
// nobgp-router#730, which drops the old function once nothing calls it.
//
// The fallback is taken on ANY error rather than only the missing-function one:
// guessing wrong about what PostgREST returns for an un-migrated function would
// blank every avatar in exactly the window this exists for. But PGRST202 ("not
// found in the schema cache") is the only error that means "not migrated yet", so
// anything else is warned about instead of passing for it — and so is a failing
// fallback, which leaves every unnamed personal org labeled a bare "Organization".
async function getOwners(): Promise<OwnerRow[]> {
  const { data, error } = await supabase.rpc('get_org_owners')
  if (!error) return (data ?? []) as OwnerRow[]
  if (error.code !== 'PGRST202') console.warn('get_org_owners failed, falling back to avatars', error)

  const { data: avatars, error: avatarError } = await supabase.rpc('get_org_owner_avatars')
  if (avatarError) console.warn(avatarError)
  return (avatars ?? []).map(r => ({ org_id: r.org_id, full_name: null, email: null, avatar_url: r.avatar_url }))
}

// Label an org for display. A team org always has a name. A personal org's name is
// NULL until its owner picks one (router 0091), and then it derives: the owner's
// name, else their email — so your own reads "Benoit Menendez" rather than a
// generic "Personal", and two personal orgs (yours + one you were invited into)
// stay distinguishable. `||`, not `??`: an empty name means unnamed too.
//
// This mirrors public.org_display_name(), which is the same rule for the router's
// own prose (invite emails). Two expressions of one rule, because only this side
// can follow a profile edit without a round trip. The owner part is `userText`,
// the app's one person label (app#401).
export function orgLabel(org: Pick<OrganizationRecord, 'name' | 'ownerName' | 'ownerEmail'>): string {
  return org.name || userText({ name: org.ownerName, email: org.ownerEmail }, 'Organization')
}

// The active org's roster, for the graph's owner labels and the members page. Only an Owner or
// Admin may list members (PERMISSIONS.md § 5 Org, router#1765, app#381), so a Member does not
// ask: the router would refuse. Clear the roster instead, so a roster read in another org, or
// before a demotion, is not kept. The graph then labels a co-member's network "Member".
function loadRoster(orgId: string, role: string | null) {
  if (mayListMembers(role as OrgRole)) void useOrgMembers.getState().get(orgId)
  else useOrgMembers.getState().reset()
}

export interface OrganizationState {
  organizations: OrganizationRecord[]
  activeOrgId: string | null
  role: string | null // caller's role in the active org (mirrors tools/authz.go)
  set: (state: Partial<OrganizationState>) => void
  get: () => Promise<void>
  setActiveOrg: (id: string) => void
  /**
   * The org the app made active BY ITSELF, to follow a path that names an object of that org
   * (app#478), or null. The header says so while it is set. A pick by the reader clears it.
   */
  followed: string | null
  /**
   * Makes `id` the active org because the path names an object of it (`useCloseWhenMissing`).
   * ⚠ The one place where a URL changes the active org. It is `setActiveOrg` plus the notice: the
   * active org also decides whose usage and billing the reader sees, so the change is never silent.
   */
  followTo: (id: string) => void
  create: (name: string) => Promise<void>
  rename: (id: string, name: string) => Promise<void>
  subscribe: () => () => void
  reset: () => void
}

const state: Pick<OrganizationState, 'organizations' | 'activeOrgId' | 'role' | 'followed'> = {
  organizations: [],
  activeOrgId: null,
  role: null,
  followed: null,
}

const useOrganizations = create<OrganizationState>()((set, get) => ({
  ...state,
  set,
  get: async () => {
    const userId = getUserId()
    if (!userId) return

    // RLS returns only the caller's orgs; filtering memberships by user_id drops
    // the other members' rows so each row is the caller's own role. The owner's
    // identity and avatar come from a SECURITY DEFINER RPC (profiles RLS is
    // self-only, so we can't read a co-member owner's row via PostgREST) —
    // fetched in parallel and merged per org.
    const [{ data, error }, owners] = await Promise.all([
      supabase
        .from('organization_members')
        .select('role, organizations(id, name, slug, personal)')
        .eq('user_id', userId),
      getOwners(),
    ])

    if (error) {
      console.warn(error)
      return
    }

    const ownerByOrg = new Map(owners.map(r => [r.org_id, r]))
    const organizations: OrganizationRecord[] = (data ?? [])
      .filter(m => m.organizations)
      .map(m => (({
      ...m.organizations,
      role: m.role,
      ownerName: ownerByOrg.get(m.organizations.id)?.full_name ?? null,
      ownerEmail: ownerByOrg.get(m.organizations.id)?.email ?? null,
      ownerAvatarUrl: ownerByOrg.get(m.organizations.id)?.avatar_url ?? null
    }) as OrganizationRecord))

    // Active org: the last selection if still a member, else the caller's OWN personal org.
    const active = defaultOrg(organizations, userId, localStorage.getItem(activeOrgKey(userId)))
    // The old key was shared by every user of the browser. Its value can be another user's
    // selection, so it is not migrated. Remove it.
    try { localStorage.removeItem('nobgp.activeOrgId') } catch { /* storage unavailable */ }

    set({
      organizations,
      activeOrgId: active?.id ?? null,
      role: active?.role ?? null,
    })

    // Load the active org's roster app-wide so the graph can label networks by
    // their owner (incl. co-members of a shared org), not just the current user.
    if (active?.id) loadRoster(active.id, active.role)
  },
  followTo: id => {
    if (id === get().activeOrgId || !get().organizations.some(o => o.id === id)) return
    get().setActiveOrg(id)
    set({ followed: id })
  },
  setActiveOrg: id => {
    const org = get().organizations.find(o => o.id === id)
    if (!org) return
    const userId = getUserId()
    if (userId) localStorage.setItem(activeOrgKey(userId), id)
    set({ activeOrgId: id, role: org.role, followed: null })
    // Roster for the newly-active org (graph owner labels).
    loadRoster(id, org.role)
    // Billing is per-org (status drives the lapse banner, plan drives the billing screen).
    // The summary of the previous org is forgotten at the switch, before any page effect runs.
    useBilling.getState().clearSummary()
    void useBilling.getState().get()
    // Usage headline axes re-key by org on their own, but the lazy traffic/service caches aren't
    // org-scoped — drop them so the newly-active org's node/network Traffic re-fetches.
    useUsage.getState().set({ services: {}, nodeTraffic: {}, nodeTrafficAll: null })
    // Pending invites are per-org: the previous org's list must not show until the new one loads
    // (app#408).
    useOrgInvites.getState().set({ invites: [] })
    // Re-scope the resource stores to the newly-active org (1.3). Nodes before the stores that
    // derive from the loaded node ids.
    // ⚠ Keep this set in step with controller.loadState's node-derived batch: a store
    // that derives from node ids and is missing here keeps the PREVIOUS org's rows after a
    // switch, and realtime cannot repair it (it carries changes, not existing rows) — which
    // is how node_metadata came to be blank for every node until a reload (app#132).
    void (async () => {
      await Promise.all([useNetwork.getState().get(), useNode.getState().get()])
      await Promise.all([
        useServices.getState().get(),
        useNodeMetadata.getState().get(),
        // The places a person set (nobgp-router#1976): of the new org's nodes, and its cloud places.
        useSetPlaces.getState().get(),
        useUpgrades.getState().get(),
      ])
    })()
  },
  create: async name => {
    const res = await callTool<{ org_id: string }>('org_create', { name })
    await get().get()
    if (res?.org_id) get().setActiveOrg(res.org_id)
  },
  // Rename an org, personal ones included since router 0091. Role-gated write, so it
  // routes through the router (a direct Supabase update is silently denied by
  // organizations' default-deny RLS). Not optimistic: callTool throws on rejection
  // (403, empty name, name too long, 5xx), and refetching only on success keeps the
  // store from showing a name the DB never accepted. Naming a personal org pins its
  // label — an explicit name wins over orgLabel's derivation, and there is no way
  // back to derived from here, since the router rejects an empty name.
  rename: async (id, name) => {
    await callTool('org_update', { org_id: id, name })
    await get().get()
  },
  subscribe: () => {
    const subscription = supabase
      .channel('organization_members-all')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'organization_members' },
        () => {
          void get().get()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(subscription)
    }
  },
  reset: () => set({ ...state }),
}))

export default useOrganizations
